import { RefObject, useCallback, useEffect, useRef, useState } from 'react';

import { createPortal } from 'react-dom';

import { ScissorsIcon } from 'lucide-react';

interface AssignmentCutLineProps {
  cutOffset: number;
  assignmentRef: RefObject<HTMLDivElement | null>;
}

export const AssignmentCutLine = ({ cutOffset, assignmentRef }: AssignmentCutLineProps) => {
  const [tooltipPos, setTooltipPos] = useState<{ x: number; y: number }>({ x: -9999, y: -9999 });
  const rafRef = useRef<number>(0);

  const updatePosition = useCallback(() => {
    const el = assignmentRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    if (rect.width === 0 && rect.height === 0) return;
    const remSize = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
    setTooltipPos({
      x: rect.left + cutOffset * remSize,
      y: rect.top,
    });
  }, [cutOffset, assignmentRef]);

  // Update position on every render — covers mount, cutOffset changes, and re-renders
  useEffect(() => {
    updatePosition();
    // Also schedule a post-paint update as fallback for initial mount
    rafRef.current = requestAnimationFrame(() => {
      updatePosition();
    });
    return () => cancelAnimationFrame(rafRef.current);
  }, [updatePosition]);

  // Keep position in sync during scroll
  useEffect(() => {
    const plannerContainer = document.getElementById('planner-container');
    if (!plannerContainer) return;

    const onScroll = () => {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(updatePosition);
    };

    plannerContainer.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      plannerContainer.removeEventListener('scroll', onScroll);
      cancelAnimationFrame(rafRef.current);
    };
  }, [updatePosition]);

  return (
    <>
      {/* Dashed vertical line inside the assignment */}
      <div
        className="absolute bottom-0 top-0 z-10 flex flex-col items-center"
        style={{ left: `${cutOffset}rem` }}
      >
        <div className="flex h-full border-r-2 border-dashed border-accent-foreground" />
      </div>

      {/* Scissors tooltip rendered via portal to escape overflow-hidden */}
      {createPortal(
        <div
          className="pointer-events-none fixed z-50 flex flex-col items-center"
          style={{
            left: tooltipPos.x,
            top: tooltipPos.y,
            transform: 'translate(-50%, -100%)',
          }}
        >
          <div className="rounded bg-accent-foreground px-1.5 py-1 text-background">
            <ScissorsIcon className="h-4 w-4" />
          </div>
          {/* Arrow pointing down */}
          <div className="h-0 w-0 border-x-4 border-t-4 border-x-transparent border-t-accent-foreground" />
        </div>,
        document.body
      )}
    </>
  );
};
